import React, { useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrums from "../../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import * as AuditLogsApi from "../../../services/auditLogs.service";

import Swal from "sweetalert2";
import { handleServerValidations, formateDateWithMonth } from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { Placeholder, Modal, Button, Loader } from "rsuite";

ModuleRegistry.registerModules([AllCommunityModule]);

const formatKeyLabel = (key) => {
  if (!key) return key;
  const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
  return withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
};

const renderDataDisplay = (data) => {
  if (!data) return null;

  const displayContainerStyle = {
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    padding: "15px",
    borderRadius: "8px",
    maxHeight: "450px",
    overflowY: "auto",
    fontSize: "13px",
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    border: "1px solid #cbd5e1",
    boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
    textAlign: "left",
    lineHeight: "1.6",
    wordBreak: "break-word",
  };

  const renderKeyValuePair = (key, val, idx) => {
    const formattedKey = formatKeyLabel(key);
    let valString = "";
    if (val === null || val === undefined) {
      valString = "N/A";
    } else if (typeof val === "boolean") {
      valString = val ? "True" : "False";
    } else if (Array.isArray(val)) {
      valString = val.map(item => (typeof item === "object" ? JSON.stringify(item) : String(item))).join(", ");
    } else if (typeof val === "object") {
      valString = JSON.stringify(val, null, 2);
    } else {
      valString = String(val);
    }

    return (
      <div key={idx} style={{ marginBottom: "6px" }}>
        <strong style={{ fontWeight: 700, color: "#0f172a", marginRight: "6px" }}>{formattedKey}:</strong>
        <span style={{ color: "#334155" }}>{valString}</span>
      </div>
    );
  };

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === "object" && parsed !== null) {
        return renderDataDisplay(parsed);
      }
    } catch (e) {
      // Not JSON string
    }

    const lines = data.split("\n");
    return (
      <div style={displayContainerStyle}>
        {lines.map((line, idx) => {
          const colonIndex = line.indexOf(":");
          if (colonIndex > 0) {
            const key = line.slice(0, colonIndex).trim();
            const val = line.slice(colonIndex + 1).trim();
            return renderKeyValuePair(key, val, idx);
          }
          return (
            <div key={idx} style={{ marginBottom: "6px", color: "#334155" }}>
              {line}
            </div>
          );
        })}
      </div>
    );
  }

  if (typeof data === "object" && data !== null) {
    const keys = Object.keys(data);
    if (keys.length === 0) {
      return (
        <div style={displayContainerStyle}>
          <span style={{ color: "#64748b" }}>No data</span>
        </div>
      );
    }
    return (
      <div style={displayContainerStyle}>
        {keys.map((key, idx) => renderKeyValuePair(key, data[key], idx))}
      </div>
    );
  }

  return (
    <div style={displayContainerStyle}>
      <span>{String(data)}</span>
    </div>
  );
};

const getChangedFields = (oldObj, newObj) => {
  let oldParsed = null;
  let newParsed = null;

  if (oldObj) {
    if (typeof oldObj === 'string') {
      try { oldParsed = JSON.parse(oldObj); } catch (e) { oldParsed = oldObj; }
    } else {
      oldParsed = oldObj;
    }
  }

  if (newObj) {
    if (typeof newObj === 'string') {
      try { newParsed = JSON.parse(newObj); } catch (e) { newParsed = newObj; }
    } else {
      newParsed = newObj;
    }
  }

  if (typeof oldParsed === 'string' || typeof newParsed === 'string') {
    return {
      filteredOld: oldParsed || null,
      filteredNew: newParsed || null,
    };
  }

  if (typeof oldParsed !== 'object' || oldParsed === null) oldParsed = {};
  if (typeof newParsed !== 'object' || newParsed === null) newParsed = {};

  const allKeys = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)]);
  
  const filteredOld = {};
  const filteredNew = {};

  const isDifferent = (val1, val2) => {
    if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
      return JSON.stringify(val1) !== JSON.stringify(val2);
    }
    return val1 !== val2;
  };

  allKeys.forEach(key => {
    // Exclude system auto-generated fields
    if (['updatedAt', 'createdAt'].includes(key)) {
      return;
    }

    if (isDifferent(oldParsed[key], newParsed[key])) {
      if (key in oldParsed) {
        filteredOld[key] = oldParsed[key];
      }
      if (key in newParsed) {
        filteredNew[key] = newParsed[key];
      }
    }
  });

  return { filteredOld, filteredNew };
};

const AuditLogsList = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  // Applied filters (only sent to API when user clicks Apply)
  const [appliedFilters, setAppliedFilters] = useState({ startDate: "", endDate: "", action: "" });

  const { filteredOld, filteredNew } = useMemo(() => {
    return getChangedFields(selectedLog?.oldData, selectedLog?.newData);
  }, [selectedLog]);

  // Fetch common action filter list
  const { data: filterListData } = useQuery({
    queryKey: ["audit-logs-filter-list"],
    queryFn: () => AuditLogsApi.getAuditLogFilterList(),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const actionOptions = filterListData?.data?.actions || [];


  const preStyle = {
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    padding: "15px",
    borderRadius: "8px",
    maxHeight: "450px",
    overflow: "auto",
    fontSize: "13px",
    fontFamily: "monospace",
    border: "1px solid #cbd5e1",
    boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
    textAlign: "left",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };

  const renderJsonValue = (data) => {
    if (!data) return null;
    let parsed = data;
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data);
      } catch (e) {
        return data;
      }
    }
    return JSON.stringify(parsed, null, 2);
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["audit-logs", userId, appliedFilters],
    queryFn: () => AuditLogsApi.getAuditLogs(userId, appliedFilters),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const userData = data?.data?.user || {};
  const isTeamManager = userData.role === "team_manager";

  const breadcrumbs = useMemo(() => [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    isTeamManager 
      ? { title: "Team Managers", url: "/admin/user-management/team-managers/list/1" }
      : { title: t("Club"), url: "/admin/user-management/club-admins/list/1" },
    { title: "Audit Logs", url: "" },
  ], [t, isTeamManager]);

  const colDefs = useMemo(() => [
    { 
      headerName: "Date & Time", 
      field: "createdAt", 
      minWidth: 180,
      valueFormatter: params => params.value ? formateDateWithMonth(params.value) : "N/A"
    },
    { 
      headerName: "Action", 
      field: "action", 
      minWidth: 150, 
      cellRenderer: (params) => (
        <span className="badge badge-pill badge-primary-light fw-bold text-uppercase">
          {params.value || "N/A"}
        </span>
      )
    },
    { 
      headerName: "Module", 
      field: "module", 
      minWidth: 130,
      cellRenderer: (params) => {
        const val = params.value || "N/A";
        return <span className="text-capitalize">{val}</span>;
      }
    },
    { 
      headerName: "Description", 
      field: "description", 
      minWidth: 250, 
      flex: 1,
      tooltipField: "description",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { 
      headerName: "IP Address", 
      field: "ipAddress", 
      minWidth: 140 
    },
    {
      headerName: "Action Data",
      field: "id",
      minWidth: 120,
      sortable: false,
      cellRenderer: (params) => {
        if (!params.data || !params.data.id) return null;
        return (
          <button
            className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center gap-1 fw-bold"
            style={{ padding: "2px 8px", height: "30px", marginTop: "6px" }}
            onClick={async () => {
              setModalOpen(true);
              setDetailLoading(true);
              setSelectedLog(params.data);
              try {
                const res = await AuditLogsApi.getAuditLogDetail(params.data.id);
                setSelectedLog(res?.data?.data || res?.data || params.data);
              } catch (err) {
                Swal.fire({
                  icon: "error",
                  text: handleServerValidations(err),
                  ...SWAL_SETTINGS,
                });
                setModalOpen(false);
              } finally {
                setDetailLoading(false);
              }
            }}
          >
            <i className="ace-icon-solid ion-eye mx-1"></i>
            View
          </button>
        );
      }
    }
  ], [setSelectedLog, setModalOpen, setDetailLoading]);

  const rowData = useMemo(() => {
    let list = data?.data?.logs || [];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(item => 
        (item.action && item.action.toLowerCase().includes(s)) ||
        (item.description && item.description.toLowerCase().includes(s)) ||
        (item.module && item.module.toLowerCase().includes(s))
      );
    }
    return list;
  }, [data, search]);

  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        setPerPage(params.api.paginationGetPageSize());
      }
    },
  }), [rowData, colDefs, perPage]);

  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async (format = "pdf") => {
    try {
      setDownloading(true);
      Swal.fire({
        title: t("Exporting PDF..."),
        text: t("Generating audit logs report with active filters..."),
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const filterParams = {
        startDate: startDate || appliedFilters.startDate || "",
        endDate: endDate || appliedFilters.endDate || "",
        action: actionFilter || appliedFilters.action || "",
        search: search || "",
      };

      const res = await AuditLogsApi.downloadAuditLogs(userId, filterParams, format);

      const blob = new Blob([res.data], { type: format === "csv" ? "text/csv" : "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const userNameSanitized = (userData.name || "user").replace(/\s+/g, "-").toLowerCase();
      link.setAttribute("download", `audit-logs-${userNameSanitized}-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.close();
    } catch (err) {
      console.error("Error downloading audit logs:", err);
      Swal.close();
      Swal.fire({
        icon: "error",
        text: handleServerValidations(err) || "Failed to download audit logs.",
        ...SWAL_SETTINGS,
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      startDate: startDate || "",
      endDate: endDate || "",
      action: actionFilter || "",
    });
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setActionFilter("");
    setSearch("");
    setAppliedFilters({ startDate: "", endDate: "", action: "" });
  };

  const hasActiveFilters = appliedFilters.startDate || appliedFilters.endDate || appliedFilters.action;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
              <h6 className="main-content-label mb-0">
                {userData.name ? `Audit Logs - ${userData.name} (${isTeamManager ? 'Team Manager' : 'Club Admin'})` : 'Audit Logs'}
              </h6>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div style={{ width: 220 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search logs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>
                <button
                  className="btn btn-sm text-white d-flex align-items-center"
                  style={{ backgroundColor: "#208078", borderColor: "#208078", padding: "6px 12px", fontWeight: 600 }}
                  type="button"
                  disabled={downloading}
                  onClick={() => handleDownloadPDF("pdf")}
                >
                  <i className="ri-file-pdf-fill me-1"></i>
                  Export PDF
                </button>
                <button
                  className="btn ripple btn-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  <i className="ace-icon-solid ion-arrow-return-left bigger-110 mx-1"></i>
                  {t("btn_back")}
                </button>
              </div>
            </div>

            {/* Filter Bar */}
            <div
              className="d-flex flex-wrap align-items-end gap-3 p-3 mb-3 rounded"
              style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
            >
              <div className="d-flex flex-column" style={{ minWidth: 160 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: 12, color: "#6c757d" }}>
                  Start Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>

              <div className="d-flex flex-column" style={{ minWidth: 160 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: 12, color: "#6c757d" }}>
                  End Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>

              <div className="d-flex flex-column" style={{ minWidth: 200 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: 12, color: "#6c757d" }}>
                  Filter by Action
                </label>
                <select
                  className="form-control form-control-sm"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                >
                  <option value="">All Actions</option>
                  {actionOptions.map((action) => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-end gap-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleApplyFilters}
                  style={{ height: 31 }}
                >
                  <i className="fe fe-filter me-1"></i>
                  Apply
                </button>
                {(startDate || endDate || actionFilter || hasActiveFilters) && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleClearFilters}
                    style={{ height: 31 }}
                  >
                    <i className="fe fe-x me-1"></i>
                    Clear
                  </button>
                )}
              </div>

              {hasActiveFilters && (
                <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
                  <small className="text-muted fw-semibold">Active filters:</small>
                  {appliedFilters.startDate && (
                    <span className="badge bg-info text-white" style={{ fontSize: 11 }}>
                      From: {appliedFilters.startDate}
                    </span>
                  )}
                  {appliedFilters.endDate && (
                    <span className="badge bg-info text-white" style={{ fontSize: 11 }}>
                      To: {appliedFilters.endDate}
                    </span>
                  )}
                  {appliedFilters.action && (
                    <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>
                      Action: {appliedFilters.action}
                    </span>
                  )}
                </div>
              )}
            </div>

            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg" backdrop="static">
        <Modal.Header>
          <Modal.Title style={{ fontWeight: 700, color: "#1e293b" }}>
            Activity Details: {selectedLog?.action || "View JSON"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px" }}>
          {detailLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Loader size="md" content="Loading details..." vertical />
            </div>
          ) : (
            <div className="row">
              <div className="col-md-6 mb-3">
                <h6 className="mb-2 text-danger fw-bold" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444' }}></span>
                  Before Change (Old Data)
                </h6>
                {filteredOld && (typeof filteredOld === 'string' ? filteredOld.trim().length > 0 : Object.keys(filteredOld).length > 0) ? (
                  renderDataDisplay(filteredOld)
                ) : (
                  <div className="p-3 bg-light border rounded text-muted text-center" style={{ minHeight: "150px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No previous data available (Created or No Change)
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <h6 className="mb-2 text-success fw-bold" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e' }}></span>
                  After Change (New Data)
                </h6>
                {filteredNew && (typeof filteredNew === 'string' ? filteredNew.trim().length > 0 : Object.keys(filteredNew).length > 0) ? (
                  renderDataDisplay(filteredNew)
                ) : (
                  <div className="p-3 bg-light border rounded text-muted text-center" style={{ minHeight: "150px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No new data available (Deleted or No Change)
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setModalOpen(false)} appearance="primary">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AuditLogsList;
